// ============================================================================
// PairsForm - Hook de Navigation
// ============================================================================

import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "./usePermissions";
import { buildMenu } from "../lib/navigation/menu-config";
import type { MenuSection, NavContext } from "../lib/navigation/types";
import { useUnreadMessages } from "./useUnreadMessages";
import { useMenuBadges } from "./useMenuBadges";

/**
 * Hook pour gérer la navigation selon le rôle utilisateur
 */
export function useNavigation() {
  const { user } = useAuth();
  const { allowed: allowedPermissions, casquettes } = usePermissions();
  const unreadCount = useUnreadMessages();
  const menuBadges = useMenuBadges();

  const menuSections = useMemo<MenuSection[]>(() => {
    if (!user) return [];

    const ctx: NavContext = {
      // NavContext garde un `role` primaire interne (legacy module nav) —
      // alimenté depuis roles[0]. Les helpers utilisent `roles` quand présent.
      role: user.roles[0] ?? "PARTICIPANT",
      roles: user.roles,
      organisationId: user.organisationId ?? undefined,
      organisationNom: user.organisationNom ?? undefined,
      organisationType: user.organisationType ?? undefined,
      organisationSlug: user.organisationSlug ?? null,
      estAnimateur: user.estAnimateur ?? false,
      estParticipant: user.estParticipant ?? false,
      typeAnimateur: user.typeAnimateur ?? null,
      plateformeActif: user.plateformeActif ?? false,
      // S2 : l'entrée « Mes comptes clients » n'apparaît que si ≥1 compte affecté.
      aComptesClientsAffectes: menuBadges.mesComptesClients > 0,
      aInscriptions: user.aInscriptions ?? false,
      niveauCertification: user.niveauCertification ?? null,
      parcoursElearning: user.parcoursElearning,
      allowedPermissions,
      // Union des casquettes pour les espaces PERSONNELS (cf. menu-config) :
      // une personne participante ici et animatrice là garde un menu stable
      // quelle que soit l'org active.
      casquettes,
      activeMembershipId: user.activeMembershipId,
    };

    // TODO: Récupérer les projets récents depuis une API
    const recentProjects = undefined;

    const sections = buildMenu(ctx, recentProjects);

    // Injecter les badges (le menu est un indicateur d'état — UX-REDESIGN §13.4) :
    // messages non lus + files de travail admin (candidatures, demandes de création).
    const badgesParId: Record<string, number> = {
      "seances-communication": unreadCount,
      "personnes-candidatures": menuBadges.candidaturesAnimateurs,
      "personnes-validation": menuBadges.documentsAValider,
      // Propositions de groupes Inter en attente (comptées sur la marketplace,
      // pas sur l'org active — le pilote doit être alerté avant d'y basculer).
      "plateforme-pipeline": menuBadges.propositionsAValider,
      // CRM C1.2 : affaires actives sans prochaine action ou en retard.
      "commercial-affaires": menuBadges.affairesATraiter,
    };
    for (const section of sections) {
      // Badge D2 sur le Tableau de bord (section à lien direct, sans sous-items) :
      // nb d'actions cockpit BLOCKING hors masquées.
      if (section.id === "dashboard" && menuBadges.actionsBloquantes > 0) {
        section.badge = menuBadges.actionsBloquantes;
      }
      if (!section.items) continue;
      for (const item of section.items) {
        const n = badgesParId[item.id];
        if (n && n > 0) item.badge = n;
      }
    }

    return sections;
  }, [user, unreadCount, menuBadges, allowedPermissions, casquettes]);

  return {
    menuSections,
    user,
    unreadCount,
  };
}

